import { cons, range } from "fp-ts/lib/Array";

export const getParentPath = (planIndex: number = 0, coverageIndex: number = 0) =>
  `benefitPackagingDetail.plans[${planIndex}].coverageDefs[${coverageIndex}]`;


export const getGPLUUnderWritingBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    underwritingTypeCd: `${parentPath}.underwriting.underwritingTypeCd`,
    guaranteedIssueAmount: `${parentPath}.underwriting.guaranteedIssueAmount`,
    giTimePeriod: `${parentPath}.underwriting.giTimePeriod`,
    isEOIRequired: `${parentPath}.underwriting.isEOIRequired`,
    isEOIForLateEntrant: `${parentPath}.underwriting.isEOIForLateEntrant`,
    maxUnderWritingLimitAllowance: `${parentPath}.underwriting.maxUnderWritingLimitAllowance`,
    maxDeathBenefitWithPrimaryInsuredTermLife: `${parentPath}.underwriting.maxDeathBenefitWithPrimaryInsuredTermLife`,
  };
};

export const getGPLUBenefitScheduleBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}.benefitSchedule`;

  return {
    employeeAmountPct: `${parentPath}.pctOfEmployeeAmount.employeeAmountPct`,
    benefitTypeCd: `${parentPath}.benefitTypeCd`,
    rangeValues: `${parentPath}.rangeValues`,
    multipleValues: `${parentPath}.multipleValues`,
    singleValue: `${parentPath}.singleValue`,
    benefitMinAmount: `${parentPath}.rangeValues.benefitMinAmount`,
    benefitMaxAmount: `${parentPath}.rangeValues.benefitMaxAmount`,
    flatBenefitAmount: `${parentPath}.singleValue.flatBenefitAmount`,
    incrementAmount: `${parentPath}.rangeValues.incrementAmount`,
    deathBenefitOptionCd: `${parentPath}.benefitOptions.deathBenefitOptionCd`,
    maxDeathBenefitAmountAllowance: `${parentPath}.maxDeathBenefitAmountAllowance`,
    minDeathBenefitAmountAllowance: `${parentPath}.minDeathBenefitAmountAllowance`,
  };
};

export const getGPLUAccelBenefitScheduleBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}.benefitScheduleLTCChronic`;

  return {
    benefitTypeLTCChronicCd: `${parentPath}.benefitTypeLTCChronicCd`,
    inflationProtection: `${parentPath}.inflationProtection`,
    monthlyDeathBenefitPct: `${parentPath}.monthlyDeathBenefitPct`,
    maxBenefitTimePeriod: `${parentPath}.maxBenefitTimePeriod`
  };
};

export const getGPLUOptionsBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    nonForfeitureDefaultOptionCd: `${parentPath}.nonForfeitureDefaultOptionCd`,
    maxAcceleratedBenefitPct: `${parentPath}.acceleratedBenefit.maxAcceleratedBenefitPct`,
    maxAcceleratedBenefitAmount: `${parentPath}.acceleratedBenefit.maxAcceleratedBenefitAmount`,
    isAccelerateBenefitRequired: `${parentPath}.acceleratedBenefit.isRequired`,
    isPortabilityApplied: `${parentPath}.isPortabilityApplied`,

    //accel death
    isNonForfeitureApplied: `${parentPath}.isNonForfeitureApplied`
  };
};

export const getGPLUCoverageDefinitionPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    assumedParticipationPct: `${parentPath}.assumedParticipationPct`,
    childTerminationAge: `${parentPath}.childTerminationAge`,
    terminationAge: `${parentPath}.terminationAge`,
    
    //accel death
    accelDeathBenefitTypeCd: `${parentPath}.accelDeathBenefitTypeCd`,
    eliminationPeriod: `${parentPath}.eliminationPeriod`
  };
};

export const getGPLUFundingStructurePropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    contributionTypeCd: `${parentPath}.fundingStructure.contributionTypeCd`,
    minParticipantsNum: `${parentPath}.fundingStructure.minParticipantsNum`,
    payrollFrequencyCd: `${parentPath}.fundingStructure.payrollFrequencyCd`,
  };
};

export const getGPLURatingBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    rateFormatCd: `${parentPath}.ratingDetails.rateFormatCd`,
  };
};

export const getGPLUEligibilityBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    minHourlyReq: `${parentPath}.eligibilities.minHourlyReq`,
    minIssueAge: `${parentPath}.eligibilities.minIssueAge`,
    maxIssueAge: `${parentPath}.eligibilities.maxIssueAge`,
  };
};

export const getSpouseCoverageDefinitionPropertyPaths = (
  planIndex = 0,
  coverageDefIndex = 0
) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;
  return ({
    "spouseCoveredOption": `benefitPackagingDetail.plans[${planIndex}].spouseCoveredOption`,
  })
};

export const spouseTypeInfo = {
  WS: {
    title: 'WORKING SPOUSE',
    name: 'Working Spouse',
    benefitType: 'benefitTypeWsCd',
    suffix: 'Ws',
    uwType: 'GPLUSpouseUnderwritingAmountsWs',
  },
  NWS: {
    title: 'NON-WORKING SPOUSE',
    name: 'Non-Working Spouse',
    benefitType: 'benefitTypeNwsCd',
    suffix: 'Nws',
    uwType: 'GPLUSpouseUnderwritingAmountsNws',
  },
};

const uwTypeToKey = Object.entries(spouseTypeInfo).map(([key, value]) => {
  return {[value.uwType]: key}
}).reduce((acc, curr) => {
  return { ...acc, ...curr };
}, {});


export const getGPLUSpouseBenefitSchedulePropertyPaths = (planIndex = 0, coverageDefIndex = 0, benefitScheduleindex = 0, coverageOpt: string) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;
  return {
    benefitTypeCd: `${parentPath}.benefitSchedules[${benefitScheduleindex}].${spouseTypeInfo[coverageOpt].benefitType}`,
    minDeathBenefitAllowance: `${parentPath}.benefitSchedules[${benefitScheduleindex}].minDeathBenefitAllowance`,
    maxDeathBenefitAllowance: `${parentPath}.benefitSchedules[${benefitScheduleindex}].maxDeathBenefitAllowance`,
    multipleValues: `${parentPath}.benefitSchedules[${benefitScheduleindex}].multipleValues`,
    rangeValues: `${parentPath}.benefitSchedules[${benefitScheduleindex}].rangeValues`,
    benefitMinAmount: `${parentPath}.benefitSchedules[${benefitScheduleindex}].rangeValues.benefitMinAmount`,
    benefitMaxAmount: `${parentPath}.benefitSchedules[${benefitScheduleindex}].rangeValues.benefitMaxAmount`,
    singleValue: `${parentPath}.benefitSchedules[${benefitScheduleindex}].singleValue`,
    flatBenefitAmount: `${parentPath}.benefitSchedules[${benefitScheduleindex}].singleValue.flatBenefitAmount`,
    incrementAmount: `${parentPath}.benefitSchedules[${benefitScheduleindex}].rangeValues.incrementAmount`,
    deathBenefitOptionCd: `${parentPath}.benefitSchedules[${benefitScheduleindex}].benefitOptions.deathBenefitOptionCd`,
    title: `${spouseTypeInfo[coverageOpt].title}`,
    suffix: spouseTypeInfo[coverageOpt].suffix,
  };
};

export const getGPLUSpouseUnderWritingBlockPropertyPaths = (planIndex = 0, coverageDefIndex = 0, spouseUnderwritingAmountIndex = 0, type: string) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;
  const key = uwTypeToKey[type];

  return {
    guaranteedIssueAmount: `${parentPath}.underwriting.spouseUnderwritingAmounts[${spouseUnderwritingAmountIndex}].guaranteedIssueAmount`,
    maxUnderWritingLimitAllowance: `${parentPath}.underwriting.spouseUnderwritingAmounts[${spouseUnderwritingAmountIndex}].maxUnderWritingLimitAllowance`,
    benefitTypeCd: `${parentPath}.benefitSchedules[${spouseUnderwritingAmountIndex}].${spouseTypeInfo[key].benefitType}`,
    benefitMaxAmount: `${parentPath}.benefitSchedules[${spouseUnderwritingAmountIndex}].rangeValues.benefitMaxAmount`,
    flatBenefitAmount: `${parentPath}.benefitSchedules[${spouseUnderwritingAmountIndex}].singleValue.flatBenefitAmount`,
    title: spouseTypeInfo[key].title,
    uwType: type,
    spouseType : key
  };
};
export const getGPLUExclusionsAndLimitationsPropertyPaths = (planIndex = 0, coverageDefIndex = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageDefIndex)}`;

  return {
    isSuicide: `${parentPath}.exclusions.isSuicide`,
    preExConditionLimitationCd: `${parentPath}.preExLimitation.preExConditionLimitationCd`,
    lookBackPeriodCd: `${parentPath}.preExLimitation.lookBackPeriodCd`,
    preExConditionLimitationPeriodCd: `${parentPath}.preExLimitation.preExConditionLimitationPeriodCd`,
  };
};

export const getAmountValuePath = (path: string) => path.concat('.amount');
